import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Reveal from "./Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Field({ label, testid, ...props }) {
  return (
    <label className="block">
      <span className="font-mono mb-2 block text-[10px] font-bold tracking-[0.2em] text-obsidian/60">{label}</span>
      <input
        data-testid={testid}
        {...props}
        className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm text-obsidian outline-none transition-colors duration-300 placeholder:text-obsidian/35 focus:border-brand"
      />
    </label>
  );
}

export default function QuoteSection() {
  const { t, lang } = useLang();
  const [service, setService] = useState(t.quote.serviceOptions[0]);
  const [category, setCategory] = useState(t.quote.categories[0]);
  const [volume, setVolume] = useState(200);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t.quote.required);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(t.quote.invalidEmail);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quote`, {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        message: form.message,
        service_type: service,
        category,
        volume,
        lang,
      });
      toast.success(t.quote.success);
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t.quote.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="border-t border-line bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <Reveal>
          <p className="font-mono flex items-center gap-3 text-[11px] font-bold tracking-[0.25em] text-obsidian/60">
            <span className="h-2 w-2 rotate-45 bg-brand" />
            {t.quote.eyebrow}
          </p>
          <h2 className="font-display font-expanded mt-5 max-w-3xl text-3xl font-black uppercase tracking-tight text-obsidian sm:text-4xl lg:text-5xl">
            {t.quote.title}
          </h2>
          <p className="font-mono mt-4 text-xs tracking-wide text-obsidian/50">{t.quote.note}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-5" delay={0.05}>
            <div className="flex h-full flex-col rounded-2xl bg-obsidian p-8 text-paper sm:p-10">
              <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-brand">{t.quote.summaryTitle}</p>

              {service || category ? (
                <div className="mt-8 space-y-6">
                  {[
                    { k: t.quote.service, v: service, id: "service" },
                    { k: t.quote.category, v: category, id: "category" },
                    { k: t.quote.volume, v: t.quote.volumeValue(volume), id: "volume" },
                  ].map((row) => (
                    <div key={row.id} className="border-b border-paper/15 pb-4">
                      <p className="font-mono text-[10px] tracking-[0.25em] text-paper/40">{row.k}</p>
                      <p data-testid={`quote-summary-${row.id}`} className="font-display mt-1.5 text-lg font-bold sm:text-xl">
                        {row.v}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-8 text-sm text-paper/50">{t.quote.summaryEmpty}</p>
              )}

              <div className="mt-auto pt-10">
                <div className="flex items-center gap-3 rounded-xl border border-paper/15 bg-paper/5 px-5 py-4">
                  <Check className="h-4 w-4 shrink-0 text-[#10B981]" />
                  <p className="text-xs leading-relaxed text-paper/60">{t.quote.note}</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.12}>
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="rounded-2xl border border-line bg-paper p-8 sm:p-10"
            >
              <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-obsidian/60">
                {t.quote.serviceLabel.toUpperCase()}
              </p>
              <div className="mt-3 flex flex-wrap gap-2.5">
                {t.quote.serviceOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    data-testid="quote-calculator-service-option"
                    onClick={() => setService(opt)}
                    className={`rounded-full border px-4 py-2.5 text-xs font-bold transition-all duration-300 sm:text-sm ${
                      service === opt
                        ? "border-obsidian bg-obsidian text-paper"
                        : "border-obsidian/20 text-obsidian/60 hover:border-obsidian hover:text-obsidian"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <label className="block">
                  <span className="font-mono mb-2 block text-[10px] font-bold tracking-[0.2em] text-obsidian/60">
                    {t.quote.categoryLabel.toUpperCase()}
                  </span>
                  <select
                    data-testid="quote-calculator-category-select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm text-obsidian outline-none transition-colors duration-300 focus:border-brand"
                  >
                    {t.quote.categories.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="font-mono mb-2 flex items-center justify-between text-[10px] font-bold tracking-[0.2em] text-obsidian/60">
                    <span>{t.quote.volumeLabel.toUpperCase()}</span>
                    <span data-testid="quote-calculator-volume-value" className="text-brand">
                      {t.quote.volumeValue(volume)}
                    </span>
                  </span>
                  <input
                    data-testid="quote-calculator-volume-range"
                    type="range"
                    min="50"
                    max="2000"
                    step="50"
                    value={volume}
                    onChange={(e) => setVolume(Number(e.target.value))}
                    className="mt-3 w-full accent-[#E8394A]"
                  />
                </label>
              </div>

              <div className="mt-10 border-t border-line pt-8">
                <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-obsidian/60">{t.quote.formTitle}</p>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label={t.quote.name} testid="contact-form-name-input" value={form.name} onChange={set("name")} placeholder="—" />
                  <Field label={t.quote.company} testid="contact-form-company-input" value={form.company} onChange={set("company")} placeholder="—" />
                  <Field label={t.quote.email} testid="contact-form-email-input" type="email" value={form.email} onChange={set("email")} placeholder="name@mail.com" />
                  <Field label={t.quote.phone} testid="contact-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="+90 …" />
                </div>
                <label className="mt-5 block">
                  <span className="font-mono mb-2 block text-[10px] font-bold tracking-[0.2em] text-obsidian/60">
                    {t.quote.message}
                  </span>
                  <textarea
                    data-testid="contact-form-message-input"
                    value={form.message}
                    onChange={set("message")}
                    rows={4}
                    placeholder={t.quote.messagePlaceholder}
                    className="w-full resize-none rounded-lg border border-line bg-white px-4 py-3 text-sm text-obsidian outline-none transition-colors duration-300 placeholder:text-obsidian/35 focus:border-brand"
                  />
                </label>

                <motion.button
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  data-testid="quote-form-submit-button"
                  disabled={sending}
                  className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 text-sm font-bold text-obsidian transition-colors duration-300 hover:bg-obsidian hover:text-paper disabled:opacity-60 sm:w-auto"
                >
                  {sending ? t.quote.sending : t.quote.submit}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </motion.button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
